import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Loader2, Sparkles, RefreshCw, Power, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = "#39d2c0";
const DEFAULT_BANDS = [40, 80, 160, 315, 630, 1250, 2500, 5000, 10000, 16000];

export const pollMasteringTask = async (taskId, timeoutMs = 600000) => {
  const start = Date.now();
  let delay = 1500;
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(delay * 1.2, 5000);
    let res;
    try {
      res = await axios.get(`${API}/mastering/assistant/status/${taskId}`);
    } catch (e) {
      continue;
    }
    if (res.data.status === "complete") return res.data.result;
    if (res.data.status === "failed") throw new Error(res.data.error || "Processing failed - please try again");
  }
  throw new Error("Processing timed out - please try again");
};

const polar = (cx, cy, r, deg) => {
  const a = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};
const arcPath = (r, from, to) => {
  const [x1, y1] = polar(32, 32, r, from);
  const [x2, y2] = polar(32, 32, r, to);
  return `M ${x1} ${y1} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x2} ${y2}`;
};

function Knob({ label, value, min, max, step, format, onChange, testId }) {
  const dragRef = useRef(null);
  const pct = (value - min) / (max - min);
  const angle = -135 + pct * 270;
  const [nx, ny] = polar(32, 32, 18, angle);

  const onPointerDown = (e) => {
    e.preventDefault();
    dragRef.current = { y: e.clientY, v: value };
    const move = (ev) => {
      const delta = ((dragRef.current.y - ev.clientY) / 150) * (max - min);
      let nv = Math.min(max, Math.max(min, dragRef.current.v + delta));
      nv = Math.round(nv / step) * step;
      onChange(parseFloat(nv.toFixed(2)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div data-testid={testId} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", userSelect: "none" }}>
      <svg width="64" height="64" onPointerDown={onPointerDown} style={{ cursor: "ns-resize", touchAction: "none" }}>
        <circle cx="32" cy="32" r="24" fill="rgba(10,25,38,0.9)" stroke="rgba(77,217,232,0.2)" strokeWidth="1" />
        <path d={arcPath(28, -135, 135)} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" strokeLinecap="round" />
        <path d={arcPath(28, -135, angle)} fill="none" stroke={TEAL} strokeWidth="3" strokeLinecap="round" />
        <line x1="32" y1="32" x2={nx} y2={ny} stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: "0.78rem", fontWeight: 800, color: TEAL }}>{format(value)}</span>
      <span style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", letterSpacing: "1px", textTransform: "uppercase" }}>{label}</span>
    </div>
  );
}

function EQCurve({ bands, gains }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const W = c.width, H = c.height, mid = H / 2;
    ctx.clearRect(0, 0, W, H);
    const fmin = Math.log10(20), fmax = Math.log10(20000);
    const fx = (f) => ((Math.log10(f) - fmin) / (fmax - fmin)) * W;
    const gy = (g) => mid - (g / 10) * (mid - 12);
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.fillStyle = "rgba(255,255,255,0.3)";
    ctx.font = "11px sans-serif";
    [[50, "50"], [100, "100"], [500, "500"], [1000, "1k"], [5000, "5k"], [10000, "10k"]].forEach(([f, lbl]) => {
      const x = fx(f);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      ctx.fillText(lbl, x + 3, H - 6);
    });
    ctx.strokeStyle = "rgba(255,255,255,0.18)";
    ctx.beginPath(); ctx.moveTo(0, mid); ctx.lineTo(W, mid); ctx.stroke();
    const pts = [[20, gains[0]], ...bands.map((b, i) => [b, gains[i]]), [20000, gains[gains.length - 1]]]
      .map(([f, g]) => [fx(f), gy(g)]);
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i][0] + pts[i + 1][0]) / 2, my = (pts[i][1] + pts[i + 1][1]) / 2;
      ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
    }
    ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
    ctx.strokeStyle = TEAL;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.lineTo(W, mid); ctx.lineTo(0, mid); ctx.closePath();
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, "rgba(57,210,192,0.25)");
    grad.addColorStop(1, "rgba(57,210,192,0.03)");
    ctx.fillStyle = grad;
    ctx.fill();
  }, [bands, gains]);
  return <canvas ref={ref} width={760} height={190} data-testid="assistant-eq-canvas" style={{ width: "100%", height: "150px", display: "block" }} />;
}

function CorrBar({ value }) {
  const pct = ((value + 1) / 2) * 100;
  return (
    <div style={{ width: "100%" }}>
      <div style={{ position: "relative", height: "8px", borderRadius: "4px", background: "linear-gradient(90deg, #ef4444, #f59e0b, #22c55e)" }}>
        <div style={{
          position: "absolute", left: `calc(${pct}% - 2px)`, top: "-3px", width: "4px", height: "14px",
          borderRadius: "2px", background: "#fff", boxShadow: "0 0 6px rgba(255,255,255,0.7)", transition: "left 0.4s ease"
        }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px", fontSize: "0.6rem", color: "rgba(255,255,255,0.4)" }}>
        <span>-1</span><span data-testid="assistant-corr-value" style={{ color: TEAL, fontWeight: 800 }}>{value?.toFixed(2)}</span><span>+1</span>
      </div>
    </div>
  );
}

function LufsMeter({ label, value, testId }) {
  const pct = Math.max(3, Math.min(100, ((value + 30) / 30) * 100));
  return (
    <div style={{ marginBottom: "8px" }} data-testid={testId}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
        <span style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", letterSpacing: "1px" }}>{label}</span>
        <span style={{ fontSize: "0.72rem", fontWeight: 800, color: TEAL }}>{value?.toFixed(1)}</span>
      </div>
      <div style={{ height: "6px", borderRadius: "3px", background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${TEAL}, #4dd9e8)`, transition: "width 0.4s ease" }} />
      </div>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, format, onChange, testId }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
        <span style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", letterSpacing: "1px", textTransform: "uppercase" }}>{label}</span>
        <span style={{ fontSize: "0.72rem", fontWeight: 800, color: TEAL }}>{format(value)}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testId}
        style={{ width: "100%", accentColor: TEAL, cursor: "pointer" }}
      />
    </div>
  );
}

const sectionStyle = {
  background: "rgba(8,20,32,0.75)", border: "1px solid rgba(77,217,232,0.15)",
  borderRadius: "12px", padding: "14px 16px"
};
const sectionTitle = {
  fontSize: "0.66rem", fontWeight: 800, letterSpacing: "2px",
  color: "rgba(255,255,255,0.55)", margin: "0 0 12px 0", textTransform: "uppercase"
};

export default function MasteringAssistant({ session, onResult }) {
  const [presets, setPresets] = useState([]);
  const [characters, setCharacters] = useState({});
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [presetId, setPresetId] = useState("broadcast_ready");
  const [character, setCharacter] = useState("transparent");
  const [targetLufs, setTargetLufs] = useState(-14);
  const [width, setWidth] = useState(1.05);
  const [excite, setExcite] = useState(false);
  const [eqAmount, setEqAmount] = useState(1.0);
  const [highFreq, setHighFreq] = useState(8000);
  const [highGain, setHighGain] = useState(0);
  const [bypass, setBypass] = useState(false);

  const preset = presets.find((p) => p.id === presetId);

  const analyze = useCallback(async () => {
    if (!session) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/mastering/assistant/analyze`, { session_id: session.session_id });
      const result = await pollMasteringTask(res.data.task_id, 180000);
      setAnalysis(result);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Analysis failed - please try re-uploading");
    } finally {
      setAnalyzing(false);
    }
  }, [session]);

  useEffect(() => {
    axios.get(`${API}/mastering/assistant/presets`).then((res) => {
      setPresets(res.data.presets || []);
      setCharacters(res.data.characters || {});
    }).catch(() => {});
  }, []);

  useEffect(() => { setAnalysis(null); setMetrics(null); analyze(); }, [analyze]);

  const applyPreset = (id) => {
    setPresetId(id);
    const p = presets.find((x) => x.id === id);
    if (p) {
      setCharacter(p.character);
      setTargetLufs(p.target_lufs);
      setWidth(p.width);
      setExcite(p.excite);
    }
  };

  const bands = analysis?.bands || DEFAULT_BANDS;
  const gains = useMemo(() => {
    if (!analysis || !preset || bypass) return bands.map(() => 0);
    return preset.curve.map((t, i) => {
      const raw = Math.min(6, Math.max(-6, t - (analysis.track_shape[i] || 0)));
      return Math.min(8, Math.max(-8, raw * eqAmount));
    });
  }, [analysis, preset, eqAmount, bypass, bands]);

  const run = async () => {
    if (!session || processing) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/mastering/assistant/process`, {
        session_id: session.session_id, preset: presetId, character,
        target_lufs: targetLufs, width, excite, auto_eq_amount: eqAmount,
        high_freq: highFreq, high_gain: highGain, bypass
      });
      const result = await pollMasteringTask(res.data.task_id);
      setMetrics(result.assistant);
      onResult(result);
      toast.success(`✨ Assistant master complete — ${result.assistant.preset}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Assistant processing failed - please try again");
    } finally {
      setProcessing(false);
    }
  };

  const metricChip = (label, b, a, unit = "") => (
    <div style={{ flex: "1 1 150px", ...sectionStyle, padding: "10px 12px" }}>
      <div style={{ fontSize: "0.6rem", color: "rgba(255,255,255,0.45)", letterSpacing: "1px", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "0.85rem", fontWeight: 800 }}>
        <span style={{ color: "#f59e0b" }}>{b}{unit}</span>
        <span style={{ color: "rgba(255,255,255,0.35)", margin: "0 6px" }}>→</span>
        <span style={{ color: "#22c55e" }}>{a}{unit}</span>
      </div>
    </div>
  );

  return (
    <div data-testid="mastering-assistant-panel" style={{
      background: "linear-gradient(160deg, #0c1a26 0%, #0e2233 60%, #0b1e2d 100%)",
      border: "1px solid rgba(77,217,232,0.25)", borderRadius: "16px",
      padding: "20px", marginBottom: "20px", boxShadow: "0 8px 32px rgba(0,0,0,0.4)"
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
        <div style={{
          width: "34px", height: "34px", borderRadius: "50%", flexShrink: 0,
          background: "rgba(57,210,192,0.12)", border: `1px solid ${TEAL}`,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <Power className="w-4 h-4" style={{ color: TEAL }} />
        </div>
        <div style={{ flex: 1, minWidth: "160px" }}>
          <h3 style={{ margin: 0, color: "#fff", fontSize: "0.95rem", fontWeight: 800, letterSpacing: "1px" }}>MASTERING ASSISTANT</h3>
          <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.4)" }}>
            {analyzing ? "Analyzing track..." : analysis ? `Analyzed • ${analysis.duration_sec}s • ${analysis.lufs.toFixed(1)} LUFS` : "Waiting for analysis"}
          </span>
        </div>
        <select
          value={presetId}
          onChange={(e) => applyPreset(e.target.value)}
          data-testid="assistant-preset-select"
          style={{
            padding: "9px 14px", borderRadius: "9px", fontSize: "0.8rem", fontWeight: 700,
            background: "#0a1926", border: `1px solid rgba(77,217,232,0.4)`, color: TEAL,
            outline: "none", cursor: "pointer", minWidth: "190px"
          }}
        >
          {presets.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        <button
          onClick={analyze}
          disabled={analyzing}
          data-testid="assistant-reanalyze-btn"
          title="Reanalyze track"
          style={{
            display: "flex", alignItems: "center", gap: "6px", padding: "9px 14px", borderRadius: "9px",
            background: "rgba(57,210,192,0.08)", border: "1px solid rgba(77,217,232,0.3)",
            color: TEAL, fontSize: "0.72rem", fontWeight: 700, cursor: analyzing ? "wait" : "pointer"
          }}
        >
          <RefreshCw className="w-3.5 h-3.5" style={analyzing ? { animation: "spin 1s linear infinite" } : {}} /> Reanalyze
        </button>
      </div>

      {preset && (
        <p style={{ margin: "0 0 14px 0", fontSize: "0.72rem", color: "rgba(255,255,255,0.4)" }}>{preset.desc}</p>
      )}

      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }} data-testid="assistant-character-row">
        <span style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", letterSpacing: "1px", alignSelf: "center", marginRight: "4px" }}>CHARACTER</span>
        {Object.entries(characters).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setCharacter(id)}
            data-testid={`assistant-character-${id}`}
            style={{
              padding: "7px 16px", borderRadius: "999px", fontSize: "0.74rem", fontWeight: 700, cursor: "pointer",
              background: character === id ? "rgba(57,210,192,0.18)" : "rgba(255,255,255,0.04)",
              border: character === id ? `1px solid ${TEAL}` : "1px solid rgba(255,255,255,0.12)",
              color: character === id ? TEAL : "rgba(255,255,255,0.55)",
              transition: "background-color 0.15s, color 0.15s"
            }}
          >{label}</button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "14px", marginBottom: "14px" }}>
        <div style={{ ...sectionStyle, position: "relative" }}>
          <h4 style={sectionTitle}>Auto EQ</h4>
          {analyzing && !analysis ? (
            <div style={{ height: "150px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", color: TEAL }}>
              <Loader2 className="w-5 h-5" style={{ animation: "spin 1s linear infinite" }} />
              <span style={{ fontSize: "0.8rem", fontWeight: 700 }}>Analyzing frequency balance...</span>
            </div>
          ) : (
            <EQCurve bands={bands} gains={gains} />
          )}
          <div style={{ display: "flex", gap: "16px", marginTop: "10px", flexWrap: "wrap" }}>
            <SliderRow label="Auto EQ Amount" value={eqAmount} min={0} max={2} step={0.05}
              format={(v) => `${Math.round(v * 100)}%`} onChange={setEqAmount} testId="assistant-eq-amount" />
            <SliderRow label="High Freq" value={highFreq} min={2000} max={16000} step={100}
              format={(v) => `${(v / 1000).toFixed(1)} kHz`} onChange={setHighFreq} testId="assistant-high-freq" />
            <SliderRow label="High Gain" value={highGain} min={-6} max={6} step={0.1}
              format={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)} dB`} onChange={setHighGain} testId="assistant-high-gain" />
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div style={sectionStyle}>
            <h4 style={sectionTitle}>Dynamics</h4>
            <div style={{ display: "flex", gap: "18px", alignItems: "flex-start" }}>
              <Knob label="Loudness" value={targetLufs} min={-20} max={-8} step={0.5}
                format={(v) => `${v.toFixed(1)} LUFS`} onChange={setTargetLufs} testId="assistant-loudness-knob" />
              <div style={{ flex: 1 }}>
                <LufsMeter label="LUFS (measured)" value={analysis?.lufs ?? -30} testId="assistant-lufs-meter" />
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <span style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", letterSpacing: "1px" }}>TRUE PEAK</span>
                  <span data-testid="assistant-peak-value" style={{ fontSize: "0.72rem", fontWeight: 800, color: (analysis?.true_peak_db ?? -6) > -0.3 ? "#ef4444" : TEAL }}>
                    {(analysis?.true_peak_db ?? 0).toFixed(1)} dBTP
                  </span>
                </div>
                <button
                  onClick={() => setExcite(!excite)}
                  data-testid="assistant-excite-toggle"
                  style={{
                    display: "flex", alignItems: "center", gap: "6px", padding: "7px 16px", borderRadius: "999px",
                    fontSize: "0.72rem", fontWeight: 800, cursor: "pointer",
                    background: excite ? "rgba(245,158,11,0.18)" : "rgba(255,255,255,0.04)",
                    border: excite ? "1px solid #f59e0b" : "1px solid rgba(255,255,255,0.12)",
                    color: excite ? "#f59e0b" : "rgba(255,255,255,0.5)"
                  }}
                >
                  <Zap className="w-3.5 h-3.5" /> Excite {excite ? "ON" : "OFF"}
                </button>
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <h4 style={sectionTitle}>Spread</h4>
            <div style={{ display: "flex", gap: "18px", alignItems: "center" }}>
              <Knob label="Width" value={width} min={0.5} max={2} step={0.05}
                format={(v) => `${Math.round(v * 100)}%`} onChange={setWidth} testId="assistant-width-knob" />
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: "0.62rem", color: "rgba(255,255,255,0.45)", letterSpacing: "1px", display: "block", marginBottom: "8px" }}>CORRELATION</span>
                <CorrBar value={analysis?.correlation ?? 1} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {metrics && (
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "14px" }} data-testid="assistant-metrics">
          {metricChip("LOUDNESS (LUFS)", metrics.before.lufs, metrics.after.lufs)}
          {metricChip("TRUE PEAK (dBTP)", metrics.before.true_peak_db, metrics.after.true_peak_db)}
          {metricChip("CORRELATION", metrics.before.correlation, metrics.after.correlation)}
        </div>
      )}

      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <button
          onClick={() => setBypass(!bypass)}
          data-testid="assistant-bypass-toggle"
          style={{
            padding: "13px 22px", borderRadius: "10px", fontWeight: 800, fontSize: "0.8rem", cursor: "pointer",
            background: bypass ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.04)",
            border: bypass ? "1px solid #ef4444" : "1px solid rgba(255,255,255,0.15)",
            color: bypass ? "#ef4444" : "rgba(255,255,255,0.5)"
          }}
        >
          {bypass ? "BYPASSED" : "Bypass"}
        </button>
        <button
          onClick={run}
          disabled={processing || analyzing || !analysis}
          data-testid="assistant-process-btn"
          style={{
            flex: 1, padding: "13px", borderRadius: "10px", border: "none", fontWeight: 800, fontSize: "0.92rem",
            cursor: processing || analyzing || !analysis ? "not-allowed" : "pointer",
            background: processing || analyzing || !analysis ? "rgba(255,255,255,0.08)" : `linear-gradient(135deg, ${TEAL}, #4dd9e8)`,
            color: processing || analyzing || !analysis ? "rgba(255,255,255,0.35)" : "#04222c",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "10px"
          }}
        >
          {processing ? (
            <><Loader2 className="w-5 h-5" style={{ animation: "spin 1s linear infinite" }} /> Mastering with Assistant...</>
          ) : (
            <><Sparkles className="w-5 h-5" /> Master with Assistant</>
          )}
        </button>
      </div>
    </div>
  );
}
